// Quote version history modal (Session AAT, QUOTING DEPTH Loop 3). Shows the append-only prior-state
// snapshots for a quote, oldest first, each with a field-level diff to the state that followed it.
// An admin can delete a single historic version (never the live quote), via the two-step ConfirmButton.
import { useEffect, useState } from "react";
import { ModalShell } from "../../components/ModalShell";
import { api } from "../../lib/api";
import type { QuoteVersionItem } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Loader } from "../../components/Loader";
import { formatDateTime } from "../../lib/format";

export function QuoteVersionsModal({
  quoteId,
  quoteRef,
  isAdmin,
  onClose,
}: {
  quoteId: string;
  quoteRef: string;
  isAdmin: boolean;
  onClose: () => void;
}) {
  const [versions, setVersions] = useState<QuoteVersionItem[] | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setState("loading");
    try {
      const data = await api.quotes.versions(quoteId);
      setVersions(data.versions);
      setState("ready");
    } catch {
      setState("error");
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  async function remove(versionId: string) {
    setBusyId(versionId);
    setError(null);
    try {
      await api.quotes.deleteVersion(quoteId, versionId);
      await load();
    } catch {
      setError("Could not delete that version. Please try again.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <ModalShell ariaLabel={`Version history for ${quoteRef}`} onClose={onClose} closeOnBackdrop width="max-w-lg" panelClassName="p-5 shadow-xl">
        <h2 className="font-heading text-lg text-ele-blue">Version history: {quoteRef}</h2>
        <p className="mt-1 text-sm text-ele-body">Every time a sent quote is edited, the previous version is kept. Each entry shows what that edit changed. The live quote always shows the latest.</p>

        <div className="mt-4 max-h-96 space-y-3 overflow-y-auto">
          {state === "loading" ? (
            <Loader shape="inline" />
          ) : state === "error" ? (
            <p role="alert" className="text-sm text-ele-strong">Could not load the version history.</p>
          ) : versions && versions.length === 0 ? (
            <p className="text-sm text-ele-body">No prior versions. This quote has not been edited since it was first sent.</p>
          ) : (
            versions!.map((v) => (
              <div key={v.id} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-menu text-xs uppercase tracking-wide text-ele-blue">Version {v.versionNumber}</span>
                  <span className="text-xs text-ele-body">{formatDateTime(v.createdAt)}</span>
                </div>
                {v.changes.length === 0 ? (
                  <p className="mt-2 text-sm text-ele-body">No tracked fields changed in the next edit.</p>
                ) : (
                  <ul className="mt-2 space-y-1 text-sm">
                    {v.changes.map((c) => (
                      <li key={c.field} className="text-ele-body">
                        <span className="text-ele-strong">{c.field}:</span>{" "}
                        <span className="text-ele-danger line-through">{c.from || "(blank)"}</span>{" "}
                        <span className="text-ele-body">to</span>{" "}
                        <span className="text-ele-green">{c.to || "(blank)"}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {isAdmin && (
                  <div className="mt-2 text-right">
                    <ConfirmButton
                      onConfirm={() => remove(v.id)}
                      busy={busyId === v.id}
                      disabled={busyId === v.id}
                      className="text-xs text-ele-body hover:text-ele-danger"
                      label="Delete version"
                      title="Delete this historic version (the live quote is untouched)"
                    />
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {error && <p role="alert" className="mt-3 text-sm text-ele-danger">{error}</p>}

        <div className="mt-5 flex justify-end">
          <button type="button" onClick={onClose} className="rounded-ele border border-ele-purple-accent px-4 py-2 text-sm">Done</button>
        </div>
    </ModalShell>
  );
}
